import { Clock, Plus, CheckCircle2, Circle, Trash2, Phone, Mail, Calendar, FileText, UserCheck, Home, Eye, AlertCircle, Edit2, StickyNote } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { createTask, deleteTaskRequest, getTasks, updateTask } from "../../api";
import { updateContactReminder, deleteContactReminder } from "../../api/contact";
import { formatDateAU } from "../../utils/dateFormat";
import {
  formatMelbourneDatePart,
  formatMelbourneTimePart,
  formatMelbourneDateTime,
  formatMelbourneClock,
  datetimeLocalToUtcISOString,
} from "../../utils/melbourneTime";
import toast from "react-hot-toast";
import { getLeads } from "../../api/lead";
import { getProperties } from "../../api/agent";
import PageHeader from "../shared/PageHeader";

// Task type icons
const getTaskTypeIcon = (taskType) => {
  switch (taskType) {
    case "call":
      return <Phone className="w-4 h-4" />;
    case "email":
      return <Mail className="w-4 h-4" />;
    case "meeting":
      return <Calendar className="w-4 h-4" />;
    case "follow_up":
      return <UserCheck className="w-4 h-4" />;
    case "inspection":
    case "viewing":
      return <Home className="w-4 h-4" />;
    case "paperwork":
      return <FileText className="w-4 h-4" />;
    default:
      return <Circle className="w-4 h-4" />;
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case "high":
      return "bg-red-100 text-red-700 border-red-200";
    case "medium":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    case "low":
      return "bg-green-100 text-green-700 border-green-200";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-700";
    case "in_progress":
      return "bg-blue-100 text-blue-700";
    case "not_started":
      return "bg-gray-100 text-gray-700";
    case "cancelled":
      return "bg-red-100 text-red-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === tomorrow.toDateString()) {
    return "Tomorrow";
  } else {
    // DD/MM/YYYY — the agency is Australian; the browser default could render US
    // MM/DD/YYYY.
    return formatDateAU(date);
  }
};

// Where a task deep-links to: the person's contact profile first, then the
// linked property, then the leads list. null = not clickable.
const getTaskLink = (task) => {
  if (task.contact_id) return `/contacts/${task.contact_id}`;
  if (task.related_property) return `/properties/${task.related_property}`;
  if (task.lead) return `/leads`;
  return null;
};

export default function TasksManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);

  const [filter, setFilter] = useState("all");
  const [taskTypeFilter, setTaskTypeFilter] = useState("all");
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    priority: "medium",
    status: "not_started",
    taskType: "general",
    dueDate: "",
    reminderDate: "",
    leadId: "none",
    propertyId: "none",
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Extra states for loaders and delete confirm
  const [adding, setAdding] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [viewTask, setViewTask] = useState(null); // detail popup for tasks with no linked record
  const [editingTaskId, setEditingTaskId] = useState(null); // set while the Add/Edit dialog is editing an existing task
  // Set while the dialog is editing a contact call-back reminder rather than a
  // UserTask: { contactId, reminderId }. The two live on different endpoints.
  const [editingReminder, setEditingReminder] = useState(null);

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTasks();
      const tasksData = response?.data || [];
      // Ensure we always set an array
      setTasks(Array.isArray(tasksData) ? tasksData : []);
    } catch (err) {
      setTasks([]);
      setError("Failed to load tasks.");
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await getLeads({ page_size: 1000 });
      setLeads(response?.data?.results || []);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await getProperties({ page_size: 1000 });
      setProperties(response?.data?.results || []);
    } catch (error) {
      console.error("Failed to fetch properties:", error);
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchLeads();
    fetchProperties();

    // Check URL parameters for pre-filling
    const params = new URLSearchParams(location.search);
    const leadId = params.get('lead');
    const leadName = params.get('leadName');
    const propertyId = params.get('property');
    const propertyAddress = params.get('propertyAddress');

    if (leadId || propertyId) {
      setIsDialogOpen(true);
      setNewTask(prev => ({
        ...prev,
        leadId: leadId || "",
        propertyId: propertyId || "",
        title: leadName ? `Follow up with ${leadName}` : propertyAddress ? `Task for ${propertyAddress}` : "",
        taskType: leadId ? "follow_up" : propertyId ? "inspection" : "general",
      }));
    }
  }, [location.search]);

  const toggleTask = async (id) => {
    const taskToUpdate = tasks.find((task) => task.id === id);
    if (!taskToUpdate) return;

    const nextCompleted = !taskToUpdate.completed;
    const updatedData = {
      ...taskToUpdate,
      completed: nextCompleted,
      status: nextCompleted ? "completed" : "in_progress",
      // A completed reminder is never overdue.
      is_overdue: nextCompleted ? false : taskToUpdate.is_overdue,
    };

    setTogglingId(id);
    try {
      // Contact call-back reminders live on a separate endpoint — route by source
      // so completing one hits lead-management/contacts/.../reminders/ instead of
      // a non-existent task with a "reminder-<id>" id.
      if (taskToUpdate.source === "reminder") {
        await updateContactReminder(taskToUpdate.contact_id, taskToUpdate.reminder_id, {
          is_completed: nextCompleted,
        });
      } else {
        await updateTask(id, updatedData);
      }
      setTasks(tasks.map((task) => (task.id === id ? updatedData : task)));
      toast.success(taskToUpdate.source === "reminder" ? "Reminder updated" : "Task updated");
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = (task) => {
    setTaskToDelete(task);
    setDeleteConfirmOpen(true);
  };

  const deleteTask = async () => {
    if (!taskToDelete) return;
    setDeletingId(taskToDelete.id);
    try {
      if (taskToDelete.source === "reminder") {
        await deleteContactReminder(taskToDelete.contact_id, taskToDelete.reminder_id);
      } else {
        await deleteTaskRequest(taskToDelete.id);
      }
      setTasks(tasks.filter((task) => task.id !== taskToDelete.id));
      toast.success(taskToDelete.source === "reminder" ? "Reminder deleted" : "Task deleted");
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    } finally {
      setDeletingId(null);
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
    }
  };

  const emptyTaskForm = {
    title: "",
    description: "",
    priority: "medium",
    status: "not_started",
    taskType: "general",
    dueDate: "",
    reminderDate: "",
    leadId: "none",
    propertyId: "none",
  };

  const closeTaskDialog = () => {
    setIsDialogOpen(false);
    setEditingTaskId(null);
    setEditingReminder(null);
    setNewTask(emptyTaskForm);
  };

  // Populate the Add/Edit form from an existing task and open it in edit mode.
  // Reachable either from the per-row Edit icon (any task) or the viewTask
  // popup's Edit button (unlinked tasks only). leadId/propertyId prefill from
  // task.lead/task.related_property when set, so a linked task keeps its link
  // shown (and can be changed or cleared) instead of only ever starting blank.
  const openEditTask = (task) => {
    // A reminder row is a ContactReminder, not a UserTask. It only has a title,
    // a description and a due date/time, and it saves to the contact's reminder
    // endpoint. "If you got the date wrong or want to delete it you should be
    // able to" (Simon Abbott, CRM-2026-00074, 21 Sep 2026): the Edit icon used to
    // be hidden on these rows, so a wrong date could only be deleted and re-set.
    if (task.source === "reminder") {
      setEditingReminder({ contactId: task.contact_id, reminderId: task.reminder_id });
      setEditingTaskId(task.id);
      setNewTask({
        ...emptyTaskForm,
        title: task.task_name || "",
        description: task.description || "",
        taskType: "call",
        priority: "medium",
        status: task.completed ? "completed" : "not_started",
        // Melbourne wall-clock time, the way it was entered and the way the rest
        // of the CRM reads it. A browser-local read shifts a 9am call-back for
        // anyone working outside Melbourne.
        reminderDate: task.due_date
          ? `${formatMelbourneDatePart(task.due_date)}T${formatMelbourneTimePart(task.due_date)}`
          : "",
      });
      setViewTask(null);
      setIsDialogOpen(true);
      return;
    }

    setEditingReminder(null);
    setNewTask({
      title: task.task_name || "",
      description: task.description || "",
      priority: task.priority || "medium",
      status: task.status || "not_started",
      taskType: task.task_type || "general",
      dueDate: task.due_date || "",
      // DateTimeField serialises with seconds/offset; datetime-local only
      // accepts "YYYY-MM-DDTHH:mm".
      reminderDate: task.reminder_date ? task.reminder_date.slice(0, 16) : "",
      leadId: task.lead != null ? String(task.lead) : "none",
      propertyId: task.related_property != null ? String(task.related_property) : "none",
    });
    setEditingTaskId(task.id);
    setViewTask(null);
    setIsDialogOpen(true);
  };

  const addTask = async () => {
    if (!newTask.title.trim()) {
      toast.error(editingReminder ? "Reminder title is required" : "Task title is required");
      return;
    }

    // Moving a reminder: PATCH the contact's reminder instead of PUTting a task
    // that doesn't exist (a reminder row's id is "reminder-<id>").
    if (editingReminder) {
      if (!newTask.reminderDate) {
        toast.error("Pick the date and time for this reminder");
        return;
      }
      const dueISO = datetimeLocalToUtcISOString(newTask.reminderDate);
      if (!dueISO) {
        toast.error("Pick a valid date and time");
        return;
      }
      setAdding(true);
      try {
        await updateContactReminder(editingReminder.contactId, editingReminder.reminderId, {
          title: newTask.title,
          description: newTask.description || "",
          due_date: dueISO,
        });
        const now = new Date().toISOString();
        setTasks((prev) => prev.map((t) => (t.id === editingTaskId
          ? {
            ...t,
            task_name: newTask.title,
            description: newTask.description || "",
            due_date: dueISO,
            reminder_date: dueISO,
            is_overdue: !t.completed && dueISO < now,
          }
          : t)));
        toast.success("Reminder updated");
        closeTaskDialog();
      } catch (error) {
        console.error("Error updating reminder:", error);
        toast.error("Failed to update reminder");
      } finally {
        setAdding(false);
      }
      return;
    }

    const taskData = {
      task_name: newTask.title,
      description: newTask.description,
      priority: newTask.priority,
      status: newTask.status,
      task_type: newTask.taskType,
      due_date: newTask.dueDate || null,
      reminder_date: newTask.reminderDate || null,
      lead: newTask.leadId && newTask.leadId !== "none" ? newTask.leadId : null,
      related_property: newTask.propertyId && newTask.propertyId !== "none" ? newTask.propertyId : null,
      // Only set on create - the PUT is partial on the backend, and editing a
      // task must not silently un-complete it.
      ...(editingTaskId ? {} : { completed: false }),
    };

    setAdding(true);
    try {
      if (editingTaskId) {
        // The PUT endpoint returns just a confirmation message, not the
        // updated record, so merge the known fields into local state instead
        // and recompute the overdue flag the same way the backend does.
        await updateTask(editingTaskId, taskData);
        const todayISO = new Date().toISOString().slice(0, 10);
        setTasks((prev) => prev.map((t) => {
          if (t.id !== editingTaskId) return t;
          const merged = { ...t, ...taskData };
          merged.is_overdue = !!merged.due_date && !merged.completed && merged.due_date < todayISO;
          return merged;
        }));
        toast.success("Task updated successfully!");
      } else {
        const response = await createTask(taskData);
        // Add the newly created task to the list immediately
        if (response.data && response.data.task) {
          setTasks(prevTasks => [response.data.task, ...prevTasks]);
        } else {
          // Fallback: refresh the entire list if response doesn't include the task
          await fetchTasks();
        }
        toast.success("Task added successfully!");
      }
      closeTaskDialog();
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error(editingTaskId ? "Failed to update task" : "Failed to add task");
    } finally {
      setAdding(false);
    }
  };

  // Ensure tasks is always an array
  const tasksArray = Array.isArray(tasks) ? tasks : [];

  const filteredTasks = tasksArray.filter((task) => {
    // Filter by completion status
    if (filter === "pending" && task.completed) return false;
    if (filter === "completed" && !task.completed) return false;

    // Filter by task type
    if (taskTypeFilter !== "all" && task.task_type !== taskTypeFilter) return false;

    return true;
  });

  const stats = {
    total: tasksArray.length,
    pending: tasksArray.filter((t) => !t.completed).length,
    completed: tasksArray.filter((t) => t.completed).length,
    overdue: tasksArray.filter((t) => !t.completed && t.is_overdue).length,
  };

  return (
    <div className="w-full">
      <PageHeader
        title="Tasks & Activities"
        subtitle="Manage your daily tasks and follow-ups"
        action={
          <Dialog open={isDialogOpen} onOpenChange={(open) => (open ? setIsDialogOpen(true) : closeTaskDialog())}>
            <DialogTrigger asChild>
              <button className="bg-[#55C5D0] px-4 py-2 rounded-xl text-white flex items-center space-x-2 transition-transform hover:bg-[#3bb3be] font-medium">
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl max-h-[95vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingReminder
                    ? "Change Reminder"
                    : (editingTaskId ? "Edit Task" : "Add New Task")}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 pt-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {editingReminder ? "Reminder *" : "Task Title *"}
                  </label>
                  <Input
                    placeholder={editingReminder ? "What is this call-back about?" : "Task title"}
                    value={newTask.title}
                    onChange={(e) =>
                      setNewTask({ ...newTask, title: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Description</label>
                  <Textarea
                    placeholder="Description (optional)"
                    value={newTask.description}
                    onChange={(e) =>
                      setNewTask({
                        ...newTask,
                        description: e.target.value,
                      })
                    }
                    rows={2}
                  />
                </div>

                {/* A reminder has none of these: no type, priority, status,
                    lead or property - just what it says and when it is due.
                    Showing them on a reminder invited edits that cannot save. */}
                {!editingReminder && (
                  <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Task Type</label>
                      <Select
                        value={newTask.taskType}
                        onValueChange={(value) =>
                          setNewTask({ ...newTask, taskType: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="call">Phone Call</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="meeting">Meeting</SelectItem>
                          <SelectItem value="follow_up">Follow Up</SelectItem>
                          <SelectItem value="inspection">Property Inspection</SelectItem>
                          <SelectItem value="viewing">Property Viewing</SelectItem>
                          <SelectItem value="paperwork">Paperwork</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1 block">Priority</label>
                      <Select
                        value={newTask.priority}
                        onValueChange={(value) =>
                          setNewTask({ ...newTask, priority: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low Priority</SelectItem>
                          <SelectItem value="medium">Medium Priority</SelectItem>
                          <SelectItem value="high">High Priority</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Status</label>
                      <Select
                        value={newTask.status}
                        onValueChange={(value) =>
                          setNewTask({ ...newTask, status: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_started">Not Started</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1 block">Due Date</label>
                      <Input
                        type="date"
                        value={newTask.dueDate}
                        onChange={(e) =>
                          setNewTask({ ...newTask, dueDate: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  </>
                )}

                <div>
                  <label className="text-sm font-medium mb-1 block">
                    {editingReminder ? "Remind me on *" : "Reminder Date/Time"}
                  </label>
                  <Input
                    type="datetime-local"
                    value={newTask.reminderDate}
                    onChange={(e) =>
                      setNewTask({ ...newTask, reminderDate: e.target.value })
                    }
                  />
                </div>

                {!editingReminder && (
                  <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Link to Lead</label>
                      <Select
                        value={newTask.leadId}
                        onValueChange={(value) =>
                          setNewTask({ ...newTask, leadId: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select lead (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Lead</SelectItem>
                          {leads.map((lead) => (
                            <SelectItem key={lead.id} value={lead.id.toString()}>
                              {lead.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-1 block">Link to Property</label>
                      <Select
                        value={newTask.propertyId}
                        onValueChange={(value) =>
                          setNewTask({ ...newTask, propertyId: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select property (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Property</SelectItem>
                          {properties.map((property) => (
                            <SelectItem key={property.id} value={property.id.toString()}>
                              {property.address}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  </>
                )}

                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    variant="outline"
                    className="rounded-xl"
                    onClick={closeTaskDialog}
                  >
                    Cancel
                  </Button>
                  <button
                    onClick={addTask}
                    disabled={adding}
                    className="bg-[#55C5D0] px-4 py-2 rounded-xl text-white hover:bg-[#3bb3be] font-medium disabled:opacity-50"
                  >
                    {adding ? (editingTaskId ? "Saving..." : "Adding...") : (editingTaskId ? "Save Changes" : "Add Task")}
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Card className="shadow-sm" style={{ border: "0.91px solid var(--c-line-soft)" }}>
          <CardContent className="p-3 sm:p-4">
            <div className="text-center">
              <p className="text-xl sm:text-2xl font-bold text-slate-900">
                {stats.total}
              </p>
              <p className="text-xs sm:text-sm text-slate-600">Total Tasks</p>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm" style={{ border: "0.91px solid var(--c-line-soft)" }}>
          <CardContent className="p-3 sm:p-4">
            <div className="text-center">
              <p className="text-xl sm:text-2xl font-bold text-[#55C5D0]">
                {stats.pending}
              </p>
              <p className="text-xs sm:text-sm text-slate-600">Pending</p>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm" style={{ border: "0.91px solid var(--c-line-soft)" }}>
          <CardContent className="p-3 sm:p-4">
            <div className="text-center">
              <p className="text-xl sm:text-2xl font-bold text-green-600">
                {stats.completed}
              </p>
              <p className="text-xs sm:text-sm text-slate-600">Completed</p>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm" style={{ border: "0.91px solid var(--c-line-soft)" }}>
          <CardContent className="p-3 sm:p-4">
            <div className="text-center">
              <p className="text-xl sm:text-2xl font-bold text-red-600">
                {stats.overdue}
              </p>
              <p className="text-xs sm:text-sm text-slate-600">Overdue</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4 px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="text-lg sm:text-xl">My Tasks</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={taskTypeFilter} onValueChange={setTaskTypeFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="call">Phone Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="viewing">Viewing</SelectItem>
                  <SelectItem value="paperwork">Paperwork</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          <Tabs value={filter} onValueChange={setFilter} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="all" className="text-xs sm:text-sm">All Tasks</TabsTrigger>
              <TabsTrigger value="pending" className="text-xs sm:text-sm">Pending</TabsTrigger>
              <TabsTrigger value="completed" className="text-xs sm:text-sm">Completed</TabsTrigger>
            </TabsList>

            <TabsContent value={filter} className="space-y-3">
              {loading ? (
                <div className="text-center py-8 text-slate-500">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 opacity-50 animate-spin rounded-full border-4 border-gray-300 border-t-[#55C5D0]"></div>
                  <p className="text-sm">Loading tasks...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-slate-500">
                  <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-red-500" />
                  <p className="text-red-500 text-sm">{error}</p>
                </div>
              ) : filteredTasks.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Circle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">No tasks found</p>
                </div>
              ) : (
                filteredTasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => { const link = getTaskLink(task); if (link) navigate(link); else setViewTask(task); }}
                    className={`p-3 sm:p-4 rounded-lg border transition-all cursor-pointer ${task.is_overdue && !task.completed
                        ? "bg-red-50 border-red-300"
                        : task.completed
                          ? "bg-slate-50 border-slate-200"
                          : "bg-white border-slate-200 hover:border-[#55C5D0]"
                      }`}
                  >
                    <div className="flex items-start space-x-2 sm:space-x-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleTask(task.id); }}
                        disabled={togglingId === task.id}
                        className="mt-0.5 text-slate-400 hover:text-[#55C5D0] transition-colors"
                      >
                        {togglingId === task.id ? (
                          <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-gray-300 border-t-[#55C5D0] rounded-full animate-spin"></div>
                        ) : task.completed ? (
                          <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                        ) : (
                          <Circle className="w-4 h-4 sm:w-5 sm:h-5" />
                        )}
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-2">
                          <div className="flex items-center gap-2">
                            <div className={`p-1 rounded ${task.is_overdue && !task.completed ? 'bg-red-100' : 'bg-[#55C5D0] bg-opacity-10'}`}>
                              {getTaskTypeIcon(task.task_type)}
                            </div>
                            <h3
                              className={`text-sm sm:text-[16px] font-medium truncate ${task.completed
                                  ? "text-slate-500 line-through"
                                  : "text-slate-900"
                                }`}
                            >
                              {task.task_name}
                            </h3>
                          </div>
                          <div className="flex items-center space-x-2">
                            {task.is_overdue && !task.completed && (
                              <Badge className="bg-red-100 text-red-700 text-[10px] sm:text-xs">
                                Overdue
                              </Badge>
                            )}
                            <Badge className={`text-[10px] sm:text-xs ${getStatusColor(task.status)}`}>
                              {task.status?.replace('_', ' ')}
                            </Badge>
                            <Badge
                              className={`text-[10px] sm:text-xs ${getPriorityColor(task.priority)}`}
                            >
                              {task.priority}
                            </Badge>
                            {/* Every row is editable, tasks and reminders alike. A
                                reminder (a ContactReminder merged into this list) opens the
                                same dialog cut down to what a reminder actually has - what
                                it says and when it is due - and saves to the contact's
                                reminder endpoint (Simon Abbott, CRM-2026-00074). Real tasks
                                get the icon regardless of whether they're linked to a
                                contact/lead/property, since clicking the row navigates away
                                before the linked ones could ever reach the viewTask popup's
                                Edit button (CG report, 2026-08-27). */}
                            <button
                              onClick={(e) => { e.stopPropagation(); openEditTask(task); }}
                              className="text-slate-400 hover:text-[#55C5D0] transition-colors p-1"
                              title={task.source === "reminder" ? "Change reminder" : "Edit task"}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); confirmDelete(task); }}
                              disabled={deletingId === task.id}
                              className="text-slate-400 hover:text-red-600 transition-colors p-1"
                              title={task.source === "reminder" ? "Delete reminder" : "Delete task"}
                            >
                              {deletingId === task.id ? (
                                <div className="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin"></div>
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>

                        {task.contact_name && (
                          <p className="flex items-center gap-1.5 text-base sm:text-lg font-bold text-slate-900 mb-1 truncate">
                            <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 shrink-0" />
                            {task.contact_name}
                          </p>
                        )}

                        {task.description && (
                          <p
                            className={`text-xs sm:text-sm mb-2 line-clamp-2 ${task.completed
                                ? "text-slate-400"
                                : "text-slate-600"
                              }`}
                          >
                            {task.description}
                          </p>
                        )}

                        {/* Most recent note on the contact this reminder is for — same
                            inline block as Who-to-Call, so the agent sees the latest
                            context without opening the contact (CG report, 2026-08-05). */}
                        {task.latest_note?.text && (
                          <div className="mt-1 mb-2 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-100 px-2 py-1.5">
                            <StickyNote className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[11px] text-gray-700 whitespace-pre-wrap break-words">{task.latest_note.text}</p>
                              {(task.latest_note.author || task.latest_note.created_at) && (
                                <p className="text-[10px] text-gray-400 mt-0.5">
                                  {task.latest_note.author}
                                  {task.latest_note.author && task.latest_note.created_at ? ' · ' : ''}
                                  {task.latest_note.created_at
                                    ? new Date(task.latest_note.created_at).toLocaleString('en-AU', {
                                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                      })
                                    : ''}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-sm text-slate-500">
                          {task.due_date && (
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span>
                                {formatDate(task.due_date)}
                                {/* A reminder is set for a time of day, not just a
                                    day - show it, or a 9am/5pm mix-up is invisible
                                    from the list. */}
                                {task.source === "reminder" && ` · ${formatMelbourneClock(task.due_date)}`}
                              </span>
                            </div>
                          )}
                          {task.lead_name && (
                            <div className="flex items-center space-x-1 text-blue-600">
                              <UserCheck className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="truncate max-w-[120px] sm:max-w-none">Lead: {task.lead_name}</span>
                            </div>
                          )}
                          {task.property_address && (
                            <div className="flex items-center space-x-1 text-green-600">
                              <Home className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="truncate max-w-[120px] sm:max-w-none">Property: {task.property_address}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Task detail popup — shown when a task has no linked person/property to open */}
      <Dialog open={!!viewTask} onOpenChange={(open) => !open && setViewTask(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getTaskTypeIcon(viewTask?.task_type)} {viewTask?.task_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm pt-2">
            {viewTask?.contact_name && (
              <p><span className="font-semibold text-slate-700">Contact:</span> {viewTask.contact_name}</p>
            )}
            {viewTask?.property_address && (
              <p><span className="font-semibold text-slate-700">Property:</span> {viewTask.property_address}</p>
            )}
            {viewTask?.description && <p className="text-slate-600">{viewTask.description}</p>}
            {viewTask?.due_date && (
              <p>
                <span className="font-semibold text-slate-700">Due:</span>{" "}
                {viewTask.source === "reminder"
                  ? formatMelbourneDateTime(viewTask.due_date)
                  : formatDateAU(viewTask.due_date)}
              </p>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Badge className={`text-xs ${getStatusColor(viewTask?.status)}`}>{viewTask?.status?.replace('_', ' ')}</Badge>
              <Badge className={`text-xs ${getPriorityColor(viewTask?.priority)}`}>{viewTask?.priority}</Badge>
            </div>
            {!viewTask?.contact_name && !viewTask?.property_address && (
              <p className="text-xs text-slate-400 pt-2">This task isn’t linked to a contact or property, so there’s no profile to open.</p>
            )}
            <div className="flex justify-end pt-2">
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => viewTask && openEditTask(viewTask)}
              >
                <Edit2 className="w-4 h-4 mr-2" /> Edit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <p>
            Are you sure you want to delete{" "}
            <span className="font-semibold">{taskToDelete?.task_name}</span>?
            This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={deleteTask}
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={deletingId === taskToDelete?.id}
            >
              {deletingId === taskToDelete?.id ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
